import { useEffect, useState, type ChangeEvent } from 'react';
import { ExternalLink, Lock, Paperclip, Trash2, Unlock } from 'lucide-react';
import { Button } from '@/design-system';
import {
  NODE_COLOR_OVERRIDES,
  NODE_STATUSES,
  isSafeNodeUrl,
  type AttachmentRef,
} from '@/domain/funnel/node-system';
import { EDGE_STYLES, type EdgeTypeId } from '@/domain/funnel/edge-system';
import { useEditorStore } from '../state/editor-store';
import {
  COLOR_LABELS,
  CATEGORY_LABELS,
  getNodeDefinition,
  STATUS_LABELS,
  type PropertyFieldDefinition,
} from '../nodes/registry/node-registry';
import {
  EDGE_STYLE_LABELS,
  getAllEdgeDefinitions,
  getEdgeDefinition,
} from '../edges/registry/edge-registry';
import { validateConnection } from '../edges/domain/validate-connection';
import {
  attachmentDownloadUrl,
  listNodeAttachments,
  removeAttachment,
  uploadNodeAttachment,
} from '../nodes/api/attachment-api';
import { useFunnelMetrics } from '../metrics/use-funnel-metrics';
import { MetricsPropertiesSection } from '../metrics/MetricsPropertiesSection';
import { EdgeConversionField } from '../metrics/EdgeConversionField';

function valueOf(data: Record<string, unknown>, key: string): string {
  return typeof data[key] === 'string' ? (data[key] as string) : '';
}

/**
 * SPEC-006 EDGE-REQ-007/T025 — a keyboard/screen-reader-friendly way to
 * create a connection, as an alternative to dragging from a canvas handle.
 */
function ConnectToPanel({ nodeId }: { nodeId: string }) {
  const graph = useEditorStore((state) => state.graph);
  const connectNodes = useEditorStore((state) => state.connectNodes);
  const [targetId, setTargetId] = useState('');
  const [edgeType, setEdgeType] = useState<EdgeTypeId>('standard');
  if (!graph) return null;
  const source = graph.nodes.find((node) => node.id === nodeId);
  if (!source || !getNodeDefinition(source.type ?? '')?.capabilities.canConnectOut) return null;
  const candidates = graph.nodes.filter((node) => {
    if (node.id === nodeId) return false;
    return validateConnection(graph, { source: nodeId, target: node.id }, edgeType).valid;
  });
  return (
    <div className="space-y-2 border-t border-border-subtle pt-3">
      <div className="text-[11px] text-text-muted">Conectar a…</div>
      <select
        aria-label="Tipo de conexão"
        value={edgeType}
        onChange={(event) => setEdgeType(event.target.value as EdgeTypeId)}
        className="w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
      >
        {getAllEdgeDefinitions().map((definition) => (
          <option key={definition.type} value={definition.type}>
            {definition.label}
          </option>
        ))}
      </select>
      <select
        aria-label="Etapa de destino"
        value={targetId}
        onChange={(event) => setTargetId(event.target.value)}
        className="w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
      >
        <option value="">Selecionar uma etapa de destino…</option>
        {candidates.map((node) => (
          <option key={node.id} value={node.id}>
            {typeof node.data.name === 'string' ? node.data.name : node.id}
          </option>
        ))}
      </select>
      <Button
        variant="secondary"
        className="w-full"
        disabled={!targetId}
        onClick={() => {
          if (targetId)
            connectNodes(
              { source: nodeId, target: targetId, sourceHandle: null, targetHandle: null },
              edgeType,
            );
        }}
      >
        Conectar
      </Button>
    </div>
  );
}

function SingleNodeProperties({ funnelId, nodeId }: { funnelId: string; nodeId: string }) {
  const node = useEditorStore((state) =>
    state.graph?.nodes.find((candidate) => candidate.id === nodeId),
  );
  const persisted = useEditorStore((state) => state.persistedNodeIds.includes(nodeId));
  const updateNodeData = useEditorStore((state) => state.updateNodeData);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<AttachmentRef[]>([]);
  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const definition = getNodeDefinition(node?.type ?? '');

  useEffect(() => {
    if (!node) return;
    setDraft(
      Object.fromEntries(
        ['name', 'description', 'url', 'responsible', 'notes'].map((key) => [
          key,
          valueOf(node.data, key),
        ]),
      ),
    );
  }, [node]);
  useEffect(() => {
    if (!persisted) {
      setAttachments([]);
      return;
    }
    void listNodeAttachments(funnelId, nodeId)
      .then(setAttachments)
      .catch(() => setAttachments([]));
  }, [funnelId, nodeId, persisted]);
  const graphMetrics = useFunnelMetrics();

  if (!node || !definition)
    return (
      <p className="text-xs text-text-muted">Os dados desconhecidos do nó {nodeId} foram preservados como somente leitura.</p>
    );

  const commitText = (field: PropertyFieldDefinition) => {
    const value = draft[field.key] ?? '';
    if (field.key === 'name' && (!value.trim() || value.trim().length > 120))
      return setError('O nome deve ter entre 1 e 120 caracteres.');
    if (
      field.kind === 'url' &&
      value &&
      !isSafeNodeUrl(value, ['localhost', '127.0.0.1'].includes(location.hostname))
    )
      return setError('Use uma URL HTTPS válida.');
    if (field.maxLength && value.length > field.maxLength)
      return setError(`${field.label} excede o limite de ${field.maxLength} caracteres.`);
    setError(null);
    updateNodeData(node.id, {
      [field.key]: field.key === 'name' ? value.trim() : value || undefined,
    });
  };
  const upload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAttachmentBusy(true);
    setError(null);
    try {
      const result = await uploadNodeAttachment(funnelId, nodeId, file);
      setAttachments((current) => [...current, result]);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Falha ao enviar o anexo.');
    } finally {
      setAttachmentBusy(false);
      event.target.value = '';
    }
  };
  const renderField = (field: PropertyFieldDefinition) => {
    if (field.kind === 'readonly')
      return (
        <div key={field.key}>
          <div className="text-[11px] text-text-muted">{field.label}</div>
          <div className="mt-1 rounded-md border border-border-subtle bg-surface-2 px-2 py-1.5 text-xs text-text-secondary">
            {field.key === 'category'
              ? CATEGORY_LABELS[definition.category]
              : valueOf(node.data, field.key)}
          </div>
        </div>
      );
    if (field.kind === 'status')
      return (
        <label key={field.key} className="block text-[11px] text-text-muted">
          Status
          <select
            aria-label="Status"
            value={valueOf(node.data, 'status') || 'active'}
            onChange={(event) => updateNodeData(node.id, { status: event.target.value })}
            className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
          >
            {NODE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
      );
    if (field.kind === 'color')
      return (
        <label key={field.key} className="block text-[11px] text-text-muted">
          Cor
          <select
            aria-label="Cor"
            value={valueOf(node.data, 'colorOverride')}
            onChange={(event) =>
              updateNodeData(node.id, { colorOverride: event.target.value || undefined })
            }
            className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
          >
            <option value="">Padrão da categoria</option>
            {NODE_COLOR_OVERRIDES.map((color) => (
              <option key={color} value={color}>
                {COLOR_LABELS[color]}
              </option>
            ))}
          </select>
        </label>
      );
    if (field.kind === 'attachments')
      return (
        <div key={field.key} className="space-y-2">
          <div className="text-[11px] text-text-muted">Anexos</div>
          {attachments.map((item) => (
            <div key={item.id} className="flex items-center gap-1 text-xs">
              <a
                href={attachmentDownloadUrl(item.id)}
                className="min-w-0 flex-1 truncate text-text-secondary hover:text-text-primary"
              >
                <Paperclip size={11} className="mr-1 inline" />
                {item.name}
              </a>
              <button
                aria-label={`Remover ${item.name}`}
                onClick={() => {
                  setAttachmentBusy(true);
                  void removeAttachment(item.id)
                    .then(() =>
                      setAttachments((current) =>
                        current.filter((candidate) => candidate.id !== item.id),
                      ),
                    )
                    .catch(() => setError('Não foi possível remover o anexo.'))
                    .finally(() => setAttachmentBusy(false));
                }}
              >
                <Trash2 size={13} />
              </button>
            </div>
          ))}
          <label
            className={`block rounded-md border border-dashed border-border p-2 text-center text-xs ${persisted ? 'cursor-pointer text-text-secondary' : 'cursor-not-allowed text-text-muted'}`}
          >
            <input
              aria-label="Enviar anexo"
              type="file"
              disabled={!persisted || attachmentBusy}
              onChange={upload}
              className="sr-only"
            />
            {persisted
              ? attachmentBusy
                ? 'Processando…'
                : 'Adicionar anexo'
              : 'Disponível após salvar o nó'}
          </label>
        </div>
      );
    const multiline = field.kind === 'textarea';
    const shared = {
      value: draft[field.key] ?? '',
      onChange: (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        setDraft((current) => ({ ...current, [field.key]: event.target.value })),
      onBlur: () => commitText(field),
      maxLength: field.maxLength,
    };
    return (
      <label key={field.key} className="block text-[11px] text-text-muted">
        {field.label}
        {multiline ? (
          <textarea
            aria-label={field.label}
            {...shared}
            rows={field.key === 'notes' ? 5 : 3}
            className="mt-1 w-full resize-y rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
          />
        ) : (
          <div className="relative">
            <input
              aria-label={field.label}
              type={field.kind === 'url' ? 'url' : 'text'}
              {...shared}
              className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 pr-7 text-xs text-text-primary"
            />
            {field.kind === 'url' && draft.url && isSafeNodeUrl(draft.url, true) ? (
              <a
                aria-label="Abrir URL"
                href={draft.url}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute right-2 top-2.5"
              >
                <ExternalLink size={12} />
              </a>
            ) : null}
          </div>
        )}
      </label>
    );
  };
  return (
    <div className="space-y-3">
      {definition.properties.map(renderField)}
      {error ? (
        <p role="alert" className="text-xs text-[#f08484]">
          {error}
        </p>
      ) : null}
      {graphMetrics && definition.supportedMetrics.length > 0 ? (
        <MetricsPropertiesSection
          nodeId={nodeId}
          supportedMetrics={definition.supportedMetrics}
          result={graphMetrics.propagation.nodeResults.get(nodeId)!}
        />
      ) : null}
      <ConnectToPanel nodeId={nodeId} />
    </div>
  );
}

function SingleEdgeProperties({ edgeId }: { edgeId: string }) {
  const edge = useEditorStore((state) =>
    state.graph?.edges.find((candidate) => candidate.id === edgeId),
  );
  const updateEdgeData = useEditorStore((state) => state.updateEdgeData);
  const changeEdgeType = useEditorStore((state) => state.changeEdgeType);
  const [label, setLabel] = useState('');

  useEffect(() => {
    setLabel(typeof edge?.data?.label === 'string' ? edge.data.label : '');
  }, [edge]);
  const graphMetrics = useFunnelMetrics();

  if (!edge) return null;
  const definition = getEdgeDefinition(edge.type ?? 'standard');
  const style = (edge.data?.style as string) === 'dashed' ? 'dashed' : 'solid';
  const animated = Boolean(edge.data?.animated);

  return (
    <div className="space-y-3">
      <label className="block text-[11px] text-text-muted">
        Tipo
        <select
          aria-label="Tipo de conexão"
          value={edge.type ?? 'standard'}
          onChange={(event) => changeEdgeType(edge.id, event.target.value as EdgeTypeId)}
          disabled={edge.type === 'unknown'}
          className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        >
          {edge.type === 'unknown' ? <option value="unknown">Desconhecido (somente leitura)</option> : null}
          {getAllEdgeDefinitions().map((candidate) => (
            <option key={candidate.type} value={candidate.type}>
              {candidate.label}
            </option>
          ))}
        </select>
      </label>
      <label className="block text-[11px] text-text-muted">
        Rótulo
        <input
          aria-label="Rótulo da conexão"
          type="text"
          value={label}
          maxLength={80}
          disabled={edge.type === 'unknown'}
          onChange={(event) => setLabel(event.target.value)}
          onBlur={() => updateEdgeData(edge.id, { label: label.trim() || undefined })}
          className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        />
      </label>
      <label className="block text-[11px] text-text-muted">
        Estilo da linha
        <select
          aria-label="Estilo da linha"
          value={style}
          disabled={edge.type === 'unknown'}
          onChange={(event) => updateEdgeData(edge.id, { style: event.target.value })}
          className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        >
          {EDGE_STYLES.map((candidate) => (
            <option key={candidate} value={candidate}>
              {EDGE_STYLE_LABELS[candidate]}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-[11px] text-text-muted">
        <input
          aria-label="Animada"
          type="checkbox"
          checked={animated}
          disabled={edge.type === 'unknown'}
          onChange={(event) => updateEdgeData(edge.id, { animated: event.target.checked })}
        />
        Animada
      </label>
      <label className="block text-[11px] text-text-muted">
        Cor
        <select
          aria-label="Cor da conexão"
          value={typeof edge.data?.colorOverride === 'string' ? edge.data.colorOverride : ''}
          disabled={edge.type === 'unknown'}
          onChange={(event) =>
            updateEdgeData(edge.id, { colorOverride: event.target.value || undefined })
          }
          className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs text-text-primary"
        >
          <option value="">Padrão do tipo</option>
          {NODE_COLOR_OVERRIDES.map((color) => (
            <option key={color} value={color}>
              {COLOR_LABELS[color]}
            </option>
          ))}
        </select>
      </label>
      {definition ? null : (
        <p className="text-xs text-text-muted">
          Esta conexão foi criada por uma versão mais nova do editor e foi preservada como somente leitura.
        </p>
      )}
      {graphMetrics ? (
        <EdgeConversionField edgeId={edgeId} result={graphMetrics.propagation.edgeResults.get(edgeId)!.conversion} />
      ) : null}
    </div>
  );
}

export function PropertiesPanelShell({ funnelId }: { funnelId: string }) {
  const selectedIds = useEditorStore((state) => state.selectedIds);
  const selectedEdgeIds = useEditorStore((state) => state.selectedEdgeIds);
  const nodes = useEditorStore((state) => state.graph?.nodes ?? []);
  const setLocked = useEditorStore((state) => state.setLocked);
  const updateSelectedNodeData = useEditorStore((state) => state.updateSelectedNodeData);
  const selected = new Set(selectedIds);
  const selectedNodes = nodes.filter((node) => selected.has(node.id));
  const allLocked = selectedNodes.length > 0 && selectedNodes.every((node) => node.locked);

  if (selectedEdgeIds.length === 1) {
    return (
      <aside
        className="w-72 shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4"
        aria-label="Painel de propriedades"
      >
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
          Propriedades
        </h2>
        <p className="mb-3 text-sm font-medium text-text-primary">Conexão selecionada</p>
        <SingleEdgeProperties edgeId={selectedEdgeIds[0]!} />
      </aside>
    );
  }
  if (selectedEdgeIds.length > 1) {
    return (
      <aside
        className="w-72 shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4"
        aria-label="Painel de propriedades"
      >
        <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
          Propriedades
        </h2>
        <p className="text-xs leading-5 text-text-muted">
          {selectedEdgeIds.length} conexões selecionadas. Selecione uma conexão para editar suas
          propriedades.
        </p>
      </aside>
    );
  }

  return (
    <aside
      className="w-72 shrink-0 overflow-y-auto border-l border-border-subtle bg-surface p-4"
      aria-label="Painel de propriedades"
    >
      <h2 className="mb-4 text-xs font-semibold uppercase tracking-[0.14em] text-text-secondary">
        Propriedades
      </h2>
      {selectedNodes.length === 0 ? (
        <p className="text-xs leading-5 text-text-muted">Selecione um nó para editar suas propriedades.</p>
      ) : (
        <div className="space-y-4">
          <p className="text-sm font-medium text-text-primary">
            {selectedNodes.length === 1
              ? 'Nó selecionado'
              : `${selectedNodes.length} nós selecionados`}
          </p>
          {selectedNodes.length === 1 ? (
            <SingleNodeProperties funnelId={funnelId} nodeId={selectedNodes[0]!.id} />
          ) : (
            <div className="space-y-3">
              <label className="block text-[11px] text-text-muted">
                Status
                <select
                  aria-label="Status da seleção"
                  defaultValue=""
                  onChange={(event) =>
                    event.target.value && updateSelectedNodeData({ status: event.target.value })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs"
                >
                  <option value="">Misto / sem alteração</option>
                  {NODE_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {STATUS_LABELS[status]}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-[11px] text-text-muted">
                Cor
                <select
                  aria-label="Cor da seleção"
                  defaultValue=""
                  onChange={(event) =>
                    updateSelectedNodeData({ colorOverride: event.target.value || undefined })
                  }
                  className="mt-1 w-full rounded-md border border-border bg-surface-2 px-2 py-1.5 text-xs"
                >
                  <option value="">Padrão da categoria / misto</option>
                  {NODE_COLOR_OVERRIDES.map((color) => (
                    <option key={color} value={color}>
                      {COLOR_LABELS[color]}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          <Button variant="secondary" className="w-full" onClick={() => setLocked(!allLocked)}>
            {allLocked ? <Unlock size={15} /> : <Lock size={15} />}
            {allLocked ? 'Desbloquear seleção' : 'Bloquear seleção'}
          </Button>
        </div>
      )}
    </aside>
  );
}
